import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoPipe } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { Gender } from '../../../api/app-types/gender.app';
import { User } from '../../../api/app-types/user.app';
import { PositionShareService, POSITION_SHARE_SERVICE } from '../../../api/services/position-share.service';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrTemplateUpdate } from '../../../core/components/template-update/template-update';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createSuccessNotification } from '../../../shared/notification/notification.factory';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { createPositionShareReadAccess } from '../../../shared/permissions/position-share-access.helper';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { AdminUserView } from '../admin-user/admin-user-view/admin-user-view';
import { PositionShareList } from '../admin-user/position-share-list/position-share-list';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';

@Component({
	selector: 'lvr-user-details',
	imports: [TranslocoPipe, MatButtonModule, AdminUserView, PositionShareList, LvrHeading, LvrPaddingDirective],
	providers: [
		{
			provide: POSITION_SHARE_SERVICE,
			useClass: PositionShareService,
		},
	],
	templateUrl: './user-details.html',
	styleUrl: './user-details.scss',
})
export class UserDetails {
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly routeData = toSignal(this.route.data);
	private readonly auth = inject(MsalService);
	private readonly env = inject(ENVIRONMENT);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);
	public readonly showDevTools = this.env.showDevTools;

	public readonly user = computed(() => this.routeData()?.['user'] as User);
	public readonly availableGenders = computed(() => this.routeData()?.['genders'] ?? []);
	private readonly currentUser = computed(() => this.user());
	private readonly currentUserObjectId = computed(() => this.user()?.objectId);
	private readonly positionShareReadAccess = createPositionShareReadAccess({
		userPermissions: this.session.permissions,
		viewedUser: this.user,
		currentUser: this.currentUser,
		currentUserObjectId: this.currentUserObjectId,
	});
	public readonly DEPLOYMENT_INFO = inject(ENVIRONMENT).deployment;
	public readonly canOpenUserSettings = hasPermission(this.session.permissions, [
		'Users.PositionShares.Read',
		'Users.PositionShares.Edit',
		'Users.PositionShares.Read.Own',
		'Users.Substitutes.Read',
		'Users.Substitutes.Edit',
		'Users.Substitutes.Read.Own',
	]);
	public readonly canReadPositionShareDetails = this.positionShareReadAccess.canReadPositionShares;
	public readonly canReadPositionShares = this.canReadPositionShareDetails;

	public userGender = computed(() => {
		const genderId = this.user()?.genderId;
		return this.availableGenders().find((g: Gender) => g.id === genderId)?.salutation ?? '-';
	});

	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	/**
	 * For development purposes only.
	 * Copies the access token to the clipboard.
	 */
	public async temporaryTokenCopy() {
		const account = this.auth.instance.getAllAccounts()[0];
		const token = await this.auth.instance.acquireTokenSilent({
			account,
			scopes: this.env.auth.scopes,
		});
		navigator.clipboard.writeText(token.accessToken ?? '');
		this.ntfy.addNotification(createSuccessNotification('Copied access token to clipboard'));
	}

	public async openScalar() {
		await this.temporaryTokenCopy();
		window.open(this.env.baseUrl + 'api/scalar', '_blank');
	}

	public openTemplateUpdate() {
		this.dialog.open(LvrTemplateUpdate, { width: '640px' });
	}
}
